import { IUploadedFile } from '../constants/interfaces';

import api from './client';

const REQUEST_BASE_URL = '/api/Files';

/**
 * Uploads a single file to the server, which then writes it to S3 and/or RustFS itself
 * (whichever the server-side kill switches currently allow) -- the browser never talks to
 * either store directly, and never handles a presigned URL.
 * @param file File to upload
 * @param params Account/folder context the server needs to resolve the storage key
 * @returns The uploaded file's record, ready to submit with the request
 */
async function uploadFile(
  file: File,
  params: { account_id: string; folder_path: string }
): Promise<IUploadedFile> {
  const formData = new FormData();
  formData.append('account_id', params.account_id);
  formData.append('folder_path', params.folder_path);
  formData.append('file', file);

  const response = await api.post<IUploadedFile>(`${REQUEST_BASE_URL}/Upload`, formData, {
    // The shared `api` instance has a 10s timeout tuned for ordinary JSON calls. The previous
    // direct-to-bucket upload went through plain fetch() and was never subject to it; now that
    // the file itself travels through this same client, a slow connection uploading even a
    // modest file could easily exceed 10s, so this call gets a much longer allowance instead.
    timeout: 120_000,
    // `api`'s instance defaults hardcode 'Content-Type': 'application/json'. Axios's own
    // transformRequest checks that header *before* deciding what to do with a FormData body --
    // if it already says application/json, axios silently JSON-stringifies the FormData instead
    // of sending it as multipart (a File serializes to "{}"), so the server never sees a real
    // file. Clearing it here (rather than merely omitting it) lets the browser set the correct
    // "multipart/form-data; boundary=..." header itself.
    headers: { 'Content-Type': undefined },
  });
  return response.data;
}

/**
 * Uploads every selected file to the server one at a time. Files are not persisted with the
 * request until this resolves -- if one file fails, the ones after it are not attempted.
 * @param files Files selected by the user
 * @param params Account/folder context the server needs to resolve each file's storage key
 * @returns The uploaded file records, ready to submit with the request
 */
export async function uploadFiles(
  files: File[],
  params: { account_id: string; folder_path: string }
): Promise<IUploadedFile[]> {
  const uploaded: IUploadedFile[] = [];
  for (const file of files) {
    uploaded.push(await uploadFile(file, params));
  }
  return uploaded;
}
